import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { buttonClasses, useIsMobile } from "../../../components/ui";
// The PNG rather than the SVG beside it, which is a bare gradient: this one
// carries the glass object the panel was drawn with, at the resolution it was
// rendered at.
// WebP rather than the PNG beside it: the same 1072x1724 pixels, 32KB
// instead of 1.2MB. A flat gradient is the worst case for a 24-bit PNG
// and close to the best case for WebP, and this panel is the whole right
// half of the screen somebody lands on — it should not arrive after they
// do.
import panelArt from "../../../assets/svg/free trial/free trial conformation page image.webp";
import { ROUTES } from "../../../routes/paths";
import { markWayInFinished } from "../../auth/services/signupArrival";
import { formatCreatedOn } from "../../brand/planFormat";
import GreenCheck from "../components/GreenCheck";
import PinnedCtaBar from "../components/PinnedCtaBar";
import { PRODUCT_NAME } from "../data/subscriptionPlans";
import { usePlans } from "../hooks/usePlans";
import {
  TRIAL_DAYS,
  TRIAL_REMINDER_DAYS,
  cycleParam,
  readTrial,
  trialLastDayOf,
  trialReminderOf,
} from "../trialState";

/** A date as the timeline writes it: `25 Aug`. No year — every stop on it is
 *  inside a fortnight, and the year is already on the card to the left. */
const shortDay = (date: Date): string =>
  date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

/** One line of the card: what it is on the left, what it says on the right. */
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-baseline justify-between gap-4">
    <dt className="text-[13px] 3xl:text-[14px] text-ink-muted">{label}</dt>
    <dd className="text-right text-[13px] 3xl:text-[14px] font-medium text-ink">
      {children}
    </dd>
  </div>
);

/** One stop on the timeline in the panel — a dot, the line down from it, and
 *  what happens on that day. */
const Stop: React.FC<{
  title: string;
  detail: string;
  /** The stop that has already happened is filled white and a size larger; the
   *  two still to come are quieter. */
  now?: boolean;
  /** The last one, so no line continues past its dot. */
  last?: boolean;
}> = ({ title, detail, now = false, last = false }) => (
  <li className="flex gap-4">
    <div className="flex flex-col items-center" aria-hidden="true">
      <span
        className={
          now
            ? "mt-1.5 h-3.5 w-3.5 flex-shrink-0 rounded-full bg-white"
            : "mt-1.5 h-3 w-3 flex-shrink-0 rounded-full bg-[#B9C7F5]"
        }
      />
      {!last && <span className="w-px flex-1 bg-white/35" />}
    </div>
    <div className={last ? "" : "pb-8"}>
      <p className="text-[16px] 3xl:text-[18px] font-medium text-white">
        {title}
      </p>
      <p className="mt-1 text-[13px] 3xl:text-[14px] leading-[1.6] text-white/80">
        {detail}
      </p>
    </div>
  </li>
);

/**
 * The trial, started — and the last screen before the app itself.
 *
 * It confirms rather than decides. What it draws is the note the detail step
 * left behind: the cycle, and the day the fourteen days run out — dates the
 * *server* set when it started the trial, carried across one navigation rather
 * than fetched again; see `trialState`. A reader who lands here with no such
 * note has not started a trial in this tab, and is sent back to the offer,
 * which asks the backend whether there is still one to be had.
 *
 * Two halves, and they answer two different questions. The left is *what has
 * happened*: a tick, the plan, and the way into the app. The right is *what
 * happens next* — the fortnight laid out as three dated stops, because the one
 * thing a free trial owes anybody is that the day it ends is not a surprise.
 *
 * It draws its own shell rather than using `TrialLayout`, which the two steps
 * before it share: the panel runs to the edge of the window on every side, and
 * a layout built around a centred column with page padding cannot do that.
 *
 * The way onward goes to the dashboard, which is where the sidebar appears for
 * the first time: all three trial screens are drawn outside `AppLayout`, so the
 * rail arriving is what tells someone they are in.
 */
const TrialStartedPage: React.FC = () => {
  const { cycle } = useParams<{ cycle: string }>();
  const cycleId = cycleParam(cycle);

  // Read once, at mount, the way the payment result screen reads its own.
  const [trial] = useState(() => (cycleId ? readTrial(cycleId) : null));

  // The plan, for its name. The list is the seller's own word for what is on
  // sale; where it has not arrived — or never does — this app's own name for
  // the product stands in, because a trial that has already started should not
  // go unnamed over a price list nobody is being shown.
  const { plans } = usePlans();
  const plan = plans.find((candidate) => candidate.id === cycleId);
  // Where the way onward goes: under the card beside the panel, or across the
  // foot of the phone under everything — see the bar at the end of this file.
  const isMobile = useIsMobile();

  // Reaching this screen with a trial behind it is what finishes a new
  // practice's way in: the dashboard is the next thing after it. Said on
  // arrival rather than on the button, because the button and the phone's
  // pinned bar lead to the same place and either may be the one pressed.
  //
  // On `trial` and not on mount: the guards below send a visit with nothing to
  // report back to the offer, and an effect runs even on the render that
  // redirects — so without this, being bounced off this screen would count as
  // having reached the end of it. A no-op for anyone who did not sign up on
  // this visit either way — see `signupArrival`.
  useEffect(() => {
    if (trial) markWayInFinished();
  }, [trial]);

  if (!cycleId) return <Navigate to={ROUTES.trial} replace />;
  if (!trial) return <Navigate to={ROUTES.trial} replace />;

  const productName = plan?.productName ?? PRODUCT_NAME;
  /** The server's `ends_at`: the instant the fortnight stops, which is the
   *  morning after the last day it covers. Nothing on this screen prints it —
   *  it is only what the day below is counted back from. */
  const serverEndsAt = new Date(trial.endsOn);
  /** The last day the trial actually covers — day fourteen, the day it started
   *  being day one. What "valid till" means, here as on a recharge. */
  const endsOn = trialLastDayOf(serverEndsAt);
  const remindsOn = trialReminderOf(endsOn);
  /** Which day of the fortnight the reminder falls on — the eleventh of
   *  fourteen, worked out rather than written, so the two constants cannot
   *  disagree with the sentence beside them. */
  const reminderDay = TRIAL_DAYS - TRIAL_REMINDER_DAYS;

  return (
    // Two columns from `lg`, stacked below it with the confirmation first: what
    // has happened outranks what happens next on a screen too narrow for both.
    // `min-h-screen` rather than `h-screen`, so a short window scrolls the page
    // instead of clipping the card.
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[1.6fr_1fr]">
      <div className="flex flex-col p-page pt-page-top sm:px-16 sm:py-10 3xl:px-20 3xl:py-14">
        {/* Centred in the half it sits in, and held to about two thirds
            of the half it sits in — the proportion the screen was drawn at.
            Capped rather than left to fill, because a card of four short rows
            stretched the width of a large monitor stops reading as a card. */}
        <div className="flex flex-1 flex-col justify-center py-10">
          <div className="w-full max-w-[550px] 3xl:max-w-[600px]">
            {/* Centred on a phone, left-aligned beside the panel.

                Two columns, the confirmation reads as the left half of a
                spread and its own left edge is the line everything on it
                lines up to. Stacked, there is no second column to line up
                with: the mark, the heading and the sentence are the whole
                width of the screen, and the design centres them. The card
                below keeps its own alignment either way, because a row of
                label-and-value is read across rather than down. */}
            <div className="max-md:flex max-md:justify-center">
              <GreenCheck />
            </div>

            <h1 className="mt-6 text-[26px] 3xl:text-[30px] font-semibold leading-tight text-brand-dark max-md:text-center">
              Your {TRIAL_DAYS}-day free trial activated!
            </h1>

            {/* The last day the trial covers, which is the same date the
                card's "Valid till" row and the panel's day-fourteen stop both
                carry. It used to name the day *after* — the server's own
                `ends_at` — on the reasoning that it is when a charge would
                fall. Two dates a day apart on one screen read as a mistake
                however each is justified, and this one was the odd one out of
                three. It is also the better of the two to drop: there is no
                charge at the end of this fortnight at all, as the card below
                says — the trial takes no payment detail and converts into
                nothing. */}
            <p className="mt-3 text-[14px] 3xl:text-[15px] leading-relaxed text-ink-muted max-md:text-center">
              Full access to everything in {productName} until{" "}
              <span className="font-semibold text-ink">
                {formatCreatedOn(endsOn)}
              </span>
              .
            </p>

            <section
              aria-labelledby="trial-card-heading"
              className="mt-7 card-surface border border-line bg-white"
            >
              <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4">
                <div className="min-w-0">
                  <h2
                    id="trial-card-heading"
                    className="text-[15px] 3xl:text-[16px] font-medium text-ink"
                  >
                    {productName}
                  </h2>
                  {/* No term under the name any more. It was kept here on the
                      argument that it decides what the charge at the end of the
                      fortnight will be — and there is no such charge: the trial
                      takes no payment detail and converts into nothing, so the
                      only honest thing this card names is the trial itself.
                      See `Plan` in the rows below, which says exactly that. */}
                </div>
                <span className="flex-shrink-0 rounded-full bg-[#E9F9F0] px-2.5 py-1 text-[11px] 3xl:text-[12px] font-semibold tracking-wide text-[#16A34A]">
                  ACTIVE
                </span>
              </div>

              <dl className="flex flex-col gap-3 px-5 py-4 3xl:py-5">
                <Row label="Plan">
                  <span className="text-brand">Trial period</span>
                </Row>
                <Row label="Start day">Today</Row>
                {/* The last day it covers, not the day the charge falls —
                    those are a day apart, and this row is the one somebody
                    reads as "how long have I got". */}
                <Row label="Valid till">{formatCreatedOn(endsOn)}</Row>
              </dl>
            </section>

            {/* Under the card on a wide screen, where this column is one
                half of a spread and ends here. On a phone the panel below is
                part of the same page rather than a second column, so a
                button in the middle of it would be the way out of a screen
                the reader has not reached the end of — it moves to the
                foot; see the bar after the panel. */}
            {!isMobile && (
              <Link
                to={ROUTES.dashboard}
                className={buttonClasses({
                  variant: "primary",
                  size: "md",
                  className: "mt-7",
                })}
              >
                Get started with Naurist
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* What happens next, on the panel. The artwork is a gradient authored as
          the whole panel, so it is the background rather than a picture inside
          it — that way it fills whatever height the window gives this column.
          Below `lg` the panel follows the confirmation rather than sitting
          beside it, and takes its height from its own content. */}
      <aside
        style={{ backgroundImage: `url("${panelArt}")` }}
        // Anchored to the bottom, not the middle. The file is a tall panel with
        // the glass object sitting on its bottom edge and bleeding off it; the
        // panel on screen is wider in proportion than the file, so `cover`
        // trims height — and centred, it takes that trim off the bottom and
        // cuts the object in half. Anchoring there spends the crop on the empty
        // gradient at the top instead, which is what that space is for.
        // Inset and rounded on a phone, where it follows the confirmation
        // rather than standing beside it: run to the edges there it reads as
        // a second screen that has started, and the card above it is already
        // telling the eye what width this page's content is. Beside the
        // confirmation it is the right half of the window and keeps every
        // edge, which is what the artwork was drawn to fill.
        //
        // The 224px under the timeline on a phone is the artwork's own band.
        // Beside the confirmation the panel is a full column and the glass
        // object has the whole height below the last stop to sit in; as a
        // card it is only as tall as its words, and `bg-bottom` then drew the
        // object straight up through them — the last stop was printed over
        // the brightest part of it and could not be read. The padding is what
        // the object needs rather than what the text wants: it is reserving
        // space for a background, which has no height of its own to push
        // anything down with.
        className="flex flex-col bg-cover bg-bottom px-8 py-14 max-md:mx-page max-md:mb-6 max-md:rounded-[8px] max-md:px-6 max-md:pb-56 max-md:pt-10 sm:px-12 sm:max-md:mx-16 3xl:px-16"
      >
        {/* Dropped from the top of the panel rather than centred in it, and by
            how much depends on the window's *height* — 80px on a short one,
            208px from 900px up. It is a height question because that is what
            the gap is competing with: the glass object anchored at the foot of
            the panel. On a tall window there is room to let the two breathe;
            on a laptop the same drop would push the last stop into it.

            `[@media(min-height:900px)]` rather than a `3xl:`-style breakpoint,
            since every named screen in this app measures width. Scoped to
            `md:` so it cannot reach a tall phone: there the panel is a card
            following the confirmation rather than a column beside it, the
            glass object is as far away as it is ever going to be, and the
            drop would only be a band of empty blue to scroll past. */}
        <h2 className="mt-20 max-md:mt-0 md:[@media(min-height:900px)]:mt-52 text-[26px] 3xl:text-[30px] font-semibold text-white">
          You&rsquo;re all set
        </h2>
        <p className="mt-2 text-[14px] 3xl:text-[15px] text-white/85">
          Here&rsquo;s how your free trial plays out - no surprises.
        </p>

        <ol className="mt-10 3xl:mt-12">
          <Stop
            now
            title="Today"
            detail={`Full ${productName} access unlocked - build plans, run reports, invite clients.`}
          />
          <Stop
            title={`Day ${reminderDay} • ${shortDay(remindsOn)}`}
            detail={`We'll remind you before the trial ends - ${TRIAL_REMINDER_DAYS} days' notice.`}
          />
          <Stop
            last
            title={`Day ${TRIAL_DAYS} • ${shortDay(endsOn)}`}
            // The design says "or switch to Free, keeping your data" here.
            // There is no Free plan to switch to — a workspace with nothing
            // running keeps its data and can read all of it, but cannot add to
            // it; see `useSubscriptionGate`. So this promises the half that is
            // true and names no tier that does not exist.
            detail="Add a payment method to stay on Pro - your data stays either way."
          />
        </ol>
      </aside>

      {/* The phone's way onward, across the foot of the screen.

          `sticky` holds it there while the confirmation and the timeline
          scroll under it, and it settles at the end of the page once they
          have been scrolled past, so the one thing there is to do on this
          screen is never more than a thumb away. Full width because it is a
          child of the page rather than of either column — the hairline is
          the edge of the content, the way it is on the price list's own
          pinned row.

          It used to be a white strip at 80% and nothing else, which is not
          glass — it is a half-transparent panel, and the blue artwork sliding
          under it arrived as a sharp picture at reduced contrast rather than
          as colour. `PinnedCtaBar` paints no fill at all now: it blurs the
          artwork instead, full width, so what is under the button is the
          gradient gone soft rather than anything laid over it. Every other
          step of the flow is the same bar, so the button does not change
          surface between picking a plan and arriving here.

          `useHiddenTabBar` inside it does nothing here: the three trial
          screens are drawn outside `AppLayout`, so there is no tab bar to
          stand down. The rail appears for the first time on the other side
          of this button. */}
      {isMobile && (
        <PinnedCtaBar className="px-5">
          {/* `lg`, which is what every other action pinned across the foot
              of a phone in this app is — Select plan on the price list, Pay
              on the checkout. A `md` button in a bar of its own reads as a
              control that has been left there rather than the thing the
              screen is for. */}
          <Link
            to={ROUTES.dashboard}
            className={buttonClasses({
              variant: "primary",
              size: "lg",
              className: "w-full",
            })}
          >
            Get started with Naurist
          </Link>
        </PinnedCtaBar>
      )}
    </div>
  );
};

export default TrialStartedPage;
